import React, { useCallback, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import DataTable from "../../common/dataTable";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import HoverPreview from "../../common/HoverPreview/HoverPreview";
import Sidemodal from "../../common/sidemodal/sidemodal";
import Dropdown from "../../common/dropDown";
import DatePicker from "react-datepicker";
import { useCapaAccess } from "./capaAccess";
import { fetchCapaAuditLog } from "./capaService";
import { filterCapaAssignableUsers } from "./capaStaffUtils";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { getAvatarColor, getInitials, stripHtml } from "../../../utils/avatarUtils";
import "react-datepicker/dist/react-datepicker.css";
import "./CapaPages.scss";

const AUDIT_FILTER_INITIAL = {
  capaNo: "",
  action: "",
  fieldName: "",
  userStaffIds: [],
  userNames: [],
  dateFrom: "",
  dateTo: "",
};

const toLower = (value) => String(value || "").trim().toLowerCase();

const capaAuditFieldDisplay = (fieldName) => {
  const raw = String(fieldName || "").trim();
  if (raw === "InvestigationEvidence") return "Investigation evidences";
  if (raw === "RcaEvidence") return "RCA evidences";
  if (raw === "VerifierInstructions") return "Instructions for verifier";
  return raw || "—";
};

const isHtmlLike = (value) => /<[^>]+>/.test(String(value || ""));

const toDateTime = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toInputDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const AuditValueCell = ({ value }) => {
  const raw = String(value || "");
  const plain = stripHtml(raw).replace(/\s+/g, " ").trim();
  if (!plain) return <span>—</span>;
  return (
    <HoverPreview
      content={raw}
      plainTextForPreview={plain}
      isHtml={isHtmlLike(raw)}
      anchorShowHtml={false}
      previewLength={99999}
      appendEllipsis={false}
      maxWidth={480}
      maxHeight={260}
    />
  );
};

const CapaAudit = () => {
  const { userData, canCapaAuditLog } = useCapaAccess();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState(AUDIT_FILTER_INITIAL);
  const [allStaffOptions, setAllStaffOptions] = useState([]);
  const [dateError, setDateError] = useState("");

  const loadAuditRows = useCallback(async () => {
    if (!userData?.OrganizationId) return;
    setLoading(true);
    try {
      const result = await fetchCapaAuditLog(userData);
      setRows(Array.isArray(result) ? result : []);
    } finally {
      setLoading(false);
    }
  }, [userData]);

  React.useEffect(() => {
    loadAuditRows();
  }, [loadAuditRows]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userData?.OrganizationId || !userData?.StaffId) return;
      try {
        const response = await api.post(API_ENDPOINTS.USER.GET_USERLIST, {
          OrganizationId: userData.OrganizationId,
          StaffId: userData.StaffId,
          Action: "Get",
          AccessCode: "",
          Password: "",
          HaveProfileViewRights: 1,
          HaveProfileEditRights: 0,
          HaveDelegationLogViewRights: 1,
          HaveDelegationLogEditRights: 0,
          HavingLineManagerRights: 1,
          HaveStudyTaskArchiveRights: 0,
          CreatedBy: userData.StaffId,
        });
        if (cancelled || response?.success !== 1) return;
        const list = Array.isArray(response?.data?.UsersActivity)
          ? response.data.UsersActivity
          : [];
        const mapped = filterCapaAssignableUsers(list)
          .map((user) => ({
            value: String(user?.StaffId || ""),
            label: String(user?.Name || user?.EmailId || user?.StaffId || "").trim(),
          }))
          .filter((opt) => opt.value && opt.label);
        setAllStaffOptions(mapped);
      } catch {
        if (!cancelled) setAllStaffOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  const assigneeOptions = useMemo(() => allStaffOptions, [allStaffOptions]);

  const filteredRows = useMemo(() => {
    const from = filters.dateFrom ? toDateTime(`${filters.dateFrom}T00:00:00`) : null;
    const to = filters.dateTo ? toDateTime(`${filters.dateTo}T23:59:59`) : null;
    return rows.filter((row) => {
      if (filters.capaNo && !toLower(row.CapaNo).includes(toLower(filters.capaNo))) return false;
      if (filters.action && !toLower(row.Action).includes(toLower(filters.action))) return false;
      if (
        filters.fieldName &&
        !toLower(row.FieldName).includes(toLower(filters.fieldName)) &&
        !toLower(capaAuditFieldDisplay(row.FieldName)).includes(toLower(filters.fieldName))
      ) {
        return false;
      }
      if (
        Array.isArray(filters.userNames) &&
        filters.userNames.length > 0 &&
        !filters.userNames.includes(String(row.UserName || "").trim())
      ) {
        return false;
      }
      if (from || to) {
        const at = toDateTime(row.At);
        if (!at) return false;
        if (from && at < from) return false;
        if (to && at > to) return false;
      }
      return true;
    });
  }, [rows, filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (String(filters.capaNo || "").trim()) count += 1;
    if (String(filters.action || "").trim()) count += 1;
    if (String(filters.fieldName || "").trim()) count += 1;
    // User filter should count once even though we store both ids and names.
    if (Array.isArray(filters.userStaffIds) && filters.userStaffIds.length > 0) count += 1;
    if (String(filters.dateFrom || "").trim()) count += 1;
    if (String(filters.dateTo || "").trim()) count += 1;
    return count;
  }, [filters]);

  const selectedFromDate = useMemo(() => {
    if (!filters.dateFrom) return null;
    return toDateTime(`${filters.dateFrom}T00:00:00`);
  }, [filters.dateFrom]);

  const selectedToDate = useMemo(() => {
    if (!filters.dateTo) return null;
    return toDateTime(`${filters.dateTo}T00:00:00`);
  }, [filters.dateTo]);

  const handleClearFilters = useCallback(() => {
    setDateError("");
    setFilters(AUDIT_FILTER_INITIAL);
  }, []);

  const isDateRangeInvalid = useMemo(() => {
    if (!selectedFromDate || !selectedToDate) return false;
    return selectedToDate < selectedFromDate;
  }, [selectedFromDate, selectedToDate]);

  const columns = [
    { label: "CAPA No", accessor: "CapaNo" },
    { label: "Date/Time", accessor: "At", type: "date" },
    { label: "Action", accessor: "Action" },
    {
      label: "Field",
      accessor: "FieldName",
      render: (row) => capaAuditFieldDisplay(row.FieldName),
    },
    { label: "Old", accessor: "OldValue", render: (row) => <AuditValueCell value={row.OldValue} /> },
    { label: "New", accessor: "NewValue", render: (row) => <AuditValueCell value={row.NewValue} /> },
    {
      label: "User",
      accessor: "UserName",
      align: "center",
      render: (row) => {
        const fullName = String(row.UserName || "").trim() || "—";
        return (
          <div className="capa-list-owner-only">
            <div
              className="capa-list-owner-avatar"
              style={{ backgroundColor: getAvatarColor(fullName) }}
              title={fullName}
            >
              {getInitials(fullName)}
            </div>
          </div>
        );
      },
    },
  ];

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }
  if (!canCapaAuditLog) return <Navigate to="/notification-center" replace />;

  return (
    <div className="capa-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "CAPA audit log" },
        ]}
        toolbarExtras={
          <div className="d-inline-block position-relative">
            <button
              type="button"
              className="actions-trigger capa-list-filter-trigger"
              onClick={() => setFilterOpen(true)}
            >
              <i className="fas fa-filter me-1" aria-hidden />
              Filter
            </button>
            {activeFilterCount > 0 ? (
              <span
                role="button"
                tabIndex={0}
                className="capa-list-filter-trigger__badge position-absolute top-0 end-0"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClearFilters();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClearFilters();
                  }
                }}
                title="Clear filters"
                aria-label="Clear filters"
                style={{
                  zIndex: 10,
                  cursor: "pointer",
                  pointerEvents: "auto",
                  top: "-0.35rem",
                  right: "-0.35rem",
                }}
              >
                {activeFilterCount} ×
              </span>
            ) : null}
          </div>
        }
        actions={[
          {
            icon: "fas fa-sync-alt",
            label: loading ? "Refreshing..." : "Refresh Data",
            onClick: loadAuditRows,
          },
        ]}
      />

      <DataTable data={filteredRows} columns={columns} sortable={["At", "CapaNo"]} showToggle />

      <Sidemodal isOpen={filterOpen} onClose={() => setFilterOpen(false)} sidewidth="26rem">
        <div className="capa-audit-filter-panel">
          <div className="capa-audit-filter-panel__header">
            <h6 className="mb-0">Filter CAPA Audit Log</h6>
          </div>
          <div className="capa-audit-filter-panel__body">
            <div className="mb-3">
              <label className="form-label small mb-1">CAPA No</label>
              <input
                className="form-control form-control-sm"
                value={filters.capaNo}
                onChange={(e) => setFilters((prev) => ({ ...prev, capaNo: e.target.value }))}
                placeholder="Search CAPA no"
              />
            </div>
            <div className="mb-3">
              <label className="form-label small mb-1">Action</label>
              <input
                className="form-control form-control-sm"
                value={filters.action}
                onChange={(e) => setFilters((prev) => ({ ...prev, action: e.target.value }))}
                placeholder="Search action"
              />
            </div>
            <div className="mb-3">
              <label className="form-label small mb-1">Field</label>
              <input
                className="form-control form-control-sm"
                value={filters.fieldName}
                onChange={(e) => setFilters((prev) => ({ ...prev, fieldName: e.target.value }))}
                placeholder="Search field"
              />
            </div>
            <div className="mb-3">
              <label className="form-label small mb-1">User</label>
              <Dropdown
                isMulti
                options={assigneeOptions}
                selected={assigneeOptions.filter((opt) =>
                  (filters.userStaffIds || []).includes(opt.value),
                )}
                onSelect={(items) =>
                  setFilters((prev) => ({
                    ...prev,
                    userStaffIds: Array.isArray(items) ? items.map((x) => String(x.value)) : [],
                    userNames: Array.isArray(items) ? items.map((x) => String(x.label).trim()) : [],
                  }))
                }
              />
            </div>
            <div className="row g-2">
              <div className="col-12">
                <label className="form-label small mb-1">From</label>
                <DatePicker
                  selected={selectedFromDate}
                  onChange={(date) => {
                    setDateError("");
                    setFilters((prev) => ({ ...prev, dateFrom: date ? toInputDate(date) : "" }));
                  }}
                  isClearable
                  placeholderText="Select from date"
                  className="form-control form-control-sm"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
              <div className="col-12">
                <label className="form-label small mb-1">To</label>
                <DatePicker
                  selected={selectedToDate}
                  onChange={(date) => {
                    setDateError("");
                    setFilters((prev) => ({ ...prev, dateTo: date ? toInputDate(date) : "" }));
                  }}
                  isClearable
                  placeholderText="Select to date"
                  className="form-control form-control-sm"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>
            {dateError ? <div className="text-danger small mt-2">{dateError}</div> : null}
          </div>
          <div className="capa-audit-filter-panel__footer">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClearFilters}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (isDateRangeInvalid) {
                  setDateError("To date should be greater than or equal to From date.");
                  return;
                }
                setDateError("");
                setFilterOpen(false);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </Sidemodal>
    </div>
  );
};

export default CapaAudit;
